/** @file Reorders a bone's children: `order` lists each child once; sibling indices become 0..n-1. */
import { z } from 'zod';
import { throwError } from '@pose2d/format/throwError';
import type { Id } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';

/** True when `order` names every id of `children` exactly once. */
const isPermutationOf = (order: readonly Id[], children: readonly Id[]): boolean =>
  order.length === children.length && new Set(order).size === order.length && order.every((id) => children.includes(id));

/** bone.reorderChildren: writes only the indices that change (dense 0..n-1, closing gaps); none changing is a no-op. */
export const command = defineCommand({
  name: 'bone.reorderChildren',
  description: 'Reorder the child bones of `parent` (name or id): `order` lists every child exactly once (names or ids); the first gets siblingIndex 0.',
  input: z.object({ parent: z.string().min(1), order: z.array(z.string().min(1)).min(1) }).strict(),
  apply(draft, input, ctx) {
    const parent = ctx.resolve('bone', input.parent);
    const children = ctx.index.children.get(parent) ?? [];
    const order = input.order.map((ref) => ctx.resolve('bone', ref));
    if (!isPermutationOf(order, children)) {
      const listed = children.map((id) => draft.bones[id]?.name ?? id).join(', ');
      throwError('VALIDATION', undefined, { path: 'order', hint: `list every child of ${input.parent} exactly once: ${listed}` });
    }
    let changed = 0;
    order.forEach((id, i) => {
      const bone = draft.bones[id];
      if (bone && bone.siblingIndex !== i) {
        bone.siblingIndex = i;
        changed++;
      }
    });
    if (changed > 0) draft.meta.updatedAt = ctx.now;
  },
});
